import { useState, useEffect } from 'react';

interface WalkthroughProps {
  userName: string;
  onComplete: () => void;
}

const STORAGE_KEY = 'hatmail_walkthrough_done';

function isTouch(): boolean {
  return window.matchMedia('(pointer: coarse)').matches;
}

export default function Walkthrough({ userName, onComplete }: WalkthroughProps) {
  const totalSteps = isTouch() ? 5 : 4;
  const [step, setStep] = useState(1);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) {
      setVisible(false);
      onComplete();
    }
  }, [onComplete]);

  if (!visible) return null;

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    onComplete();
  };

  const next = () => {
    let nextStep = step + 1;
    // Skip swipe step (step 4 in the 5-step flow) on non-touch devices
    if (!isTouch() && nextStep === 4) {
      nextStep = 5;
    }
    if (nextStep > 5) {
      finish();
    } else {
      setStep(nextStep);
    }
  };

  // Determine which highlight region to show
  const getHighlight = (): 'none' | 'status' | 'card' => {
    if (step === 2) return 'status';
    if (step === 3) return 'card';
    return 'none';
  };

  const highlight = getHighlight();

  return (
    <div className="walkthrough-overlay">
      {/* Skip button */}
      <button
        onClick={finish}
        className="walkthrough-skip"
      >
        Skip
      </button>

      {/* Dimming layer with cutout for highlighted elements */}
      {highlight !== 'none' && (
        <div className={`walkthrough-dim walkthrough-dim-${highlight}`} />
      )}

      {/* Step content */}
      <div className={`walkthrough-content ${highlight !== 'none' ? `walkthrough-positioned-${highlight}` : ''}`}>
        {step === 1 && (
          <div className="walkthrough-card">
            <h2 className="walkthrough-title">Welcome to HatMail, {userName}.</h2>
            <p className="walkthrough-text">
              Your emails are already here, and the AI has drafted responses for each one.
            </p>
            <button onClick={next} className="walkthrough-btn">
              Let's Go &rarr;
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="walkthrough-card">
            <div className="walkthrough-arrow walkthrough-arrow-up" />
            <h2 className="walkthrough-title">Your Dashboard at a Glance</h2>
            <p className="walkthrough-text">
              This tells you where things stand at a glance.
            </p>
            <button onClick={next} className="walkthrough-btn">
              Next &rarr;
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="walkthrough-card">
            <div className="walkthrough-arrow walkthrough-arrow-up" />
            <h2 className="walkthrough-title">Review Each Email</h2>
            <p className="walkthrough-text">
              Four options: <strong>Send</strong> (looks good), <strong>Edit</strong> (tweak it),{' '}
              <strong>Redraft</strong> (try again), <strong>Escalate</strong> (send to FOH review).
            </p>
            <button onClick={next} className="walkthrough-btn">
              Next &rarr;
            </button>
          </div>
        )}

        {step === 4 && isTouch() && (
          <div className="walkthrough-card">
            <h2 className="walkthrough-title">Swipe Gestures</h2>
            <div className="walkthrough-swipe-diagram">
              <div className="walkthrough-swipe-hand">
                <span className="walkthrough-swipe-arrow-right">&rarr; Send</span>
                <span className="walkthrough-swipe-arrow-left">&larr; Escalate</span>
              </div>
            </div>
            <p className="walkthrough-text">
              Swipe right to Send, Swipe left to Escalate, Tap to open and Edit.
            </p>
            <button onClick={next} className="walkthrough-btn">
              Next &rarr;
            </button>
          </div>
        )}

        {step === 5 && (
          <div className="walkthrough-card">
            <h2 className="walkthrough-title">You're Ready</h2>
            <p className="walkthrough-text">
              Every reviewed send stays in Outbox for a protected 65-second undo window before going out. You're in control.
            </p>
            <button onClick={finish} className="walkthrough-btn">
              Open My Queue &rarr;
            </button>
          </div>
        )}

        {/* Step indicator */}
        <div className="walkthrough-dots">
          {Array.from({ length: totalSteps }, (_, i) => {
            // Map visual dot index to actual step number
            let stepNum = i + 1;
            if (!isTouch() && stepNum >= 4) stepNum = 5; // skip step 4, jump to 5
            return (
              <div
                key={i}
                className={`walkthrough-dot ${step === stepNum ? 'walkthrough-dot-active' : ''}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { STORAGE_KEY };
